import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

import { api, errMsg } from "../lib/api.js";
import { logError } from "../lib/logger.js";
import { useToast } from "../context/ToastContext.js";
import { AsyncButton } from "./ui/async-button.js";
import { Textarea } from "./ui/textarea.js";
import { cn } from "../lib/utils.js";
import { shiftPeriodLabel, shiftPeriodDays, shiftWindowHours } from "../lib/shiftPeriod.js";

export interface ShiftDayRequestRow {
  id: string;
  /** The period this applies to, inclusive. A single day is start === end. */
  startDate: string;
  endDate: string;
  requestedStartHHMM: string;
  requestedEndHHMM: string;
  remark: string;
  status: string;
  /** Set once decided; the approval history names who decided and what they said. */
  approverRole?: string | null;
  approverRemark?: string | null;
  employee?: { firstName: string; lastName: string; employeeCode: string } | null;
}

/** A label over its value, one cell of the detail box. */
function Field({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) {
  return (
    <div className="flex min-w-0 items-baseline gap-2 px-3 py-1.5">
      <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-500)]">{label}</span>
      <span className={cn("truncate text-[13px] font-medium", emphasis ? "text-[var(--color-brand-600)]" : "text-[var(--color-surface-800)]")}>
        {value}
      </span>
    </div>
  );
}

/**
 * Pending "different hours for this period" asks, for whoever can decide them.
 *
 * Built on the same row as the weekly hours corrections queue -- collapsed to a
 * name and the headline figures, expanding to the detail and the decision -- so a
 * manager working down the panel meets one card shape rather than four.
 *
 * Approving is not a note on the file: getEffectiveShift serves these hours as the
 * employee's shift for every day in the period, so late, short and overtime are all
 * judged against them. The footnote says so, because an approver who thought they
 * were only recording an agreement would be deciding something else than they meant.
 */
export function ShiftDayRequestQueue({ requests, onRefresh }: {
  requests: ShiftDayRequestRow[];
  onRefresh: () => void;
}) {
  const { show } = useToast();
  const [remarks, setRemarks] = useState<Record<string, string>>({});
  const [expandedId, setExpandedId] = useState<string | null>(requests[0]?.id ?? null);

  async function decide(id: string, action: "approve" | "reject") {
    const approverRemark = remarks[id]?.trim();
    if (action === "reject" && !approverRemark) {
      show("Enter a remark for the decline", "error");
      throw new Error("validation");
    }
    try {
      await api.patch(`/shift-day-requests/${id}/${action}`, approverRemark ? { approverRemark } : {});
      show(action === "approve" ? "Approved" : "Declined", "success");
      onRefresh();
    } catch (e) {
      logError(`ShiftDayRequestQueue.${action}`, e);
      show(errMsg(e) ?? "Could not save that", "error");
      throw e;
    }
  }

  if (requests.length === 0) {
    return (
      <div className="py-5 text-center text-[13px] text-[var(--color-surface-400)]">
        No pending shift time changes
      </div>
    );
  }

  return (
    <div>
      {requests.map((r) => {
        const expanded = expandedId === r.id;
        const who = r.employee
          ? `${r.employee.firstName} ${r.employee.lastName}`.trim() || r.employee.employeeCode
          : "";
        const days = shiftPeriodDays(r.startDate, r.endDate);
        const hours = shiftWindowHours(r.requestedStartHHMM, r.requestedEndHHMM);
        return (
          <div
            key={r.id}
            className="mb-2 overflow-hidden rounded-[10px] border border-[var(--color-surface-200)] bg-[var(--color-surface-0)]"
          >
            <button
              type="button"
              onClick={() => setExpandedId(expanded ? null : r.id)}
              className="flex w-full items-center justify-between gap-2 border-none bg-transparent px-3.5 py-2.5 text-left"
            >
              <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                {expanded
                  ? <ChevronDown size={14} className="shrink-0 text-[var(--color-surface-500)]" />
                  : <ChevronRight size={14} className="shrink-0 text-[var(--color-surface-500)]" />}
                <span className="truncate text-sm font-semibold text-[var(--color-surface-900)]">{who}</span>
                {r.employee && (
                  <span className="shrink-0 text-xs text-[var(--color-surface-400)]">{r.employee.employeeCode}</span>
                )}
              </div>
              {/* The two things the decision turns on: when, and for how long. */}
              <div className="flex shrink-0 items-center gap-3">
                <span className="text-[13px] font-semibold tabular-nums text-[var(--color-surface-800)]">
                  {r.requestedStartHHMM} &ndash; {r.requestedEndHHMM}
                </span>
                <span className="text-xs text-[var(--color-surface-500)]">{shiftPeriodLabel(r.startDate, r.endDate)}</span>
              </div>
            </button>

            {expanded && (
              <div className="border-t border-[var(--color-surface-100)] px-3.5 pb-3 pt-2.5">
                <div className="mb-2 overflow-hidden rounded-lg border border-[var(--color-surface-200)] bg-[var(--color-surface-50)]">
                  <div className="grid grid-cols-2 divide-x divide-[var(--color-surface-200)]">
                    <Field
                      label="Period"
                      value={`${shiftPeriodLabel(r.startDate, r.endDate)} · ${days} ${days === 1 ? "day" : "days"}`}
                    />
                    <Field
                      label="Hours"
                      value={`${r.requestedStartHHMM} – ${r.requestedEndHHMM} · ${hours % 1 === 0 ? hours : hours.toFixed(2)} hrs`}
                      emphasis
                    />
                  </div>
                  <div className="flex items-baseline gap-2 border-t border-[var(--color-surface-200)] px-3 py-1.5">
                    <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide text-[var(--color-surface-500)]">Reason</span>
                    {/* Wraps rather than clamps: a long reason grows the box instead
                        of hiding the part the decision rests on. */}
                    <span className="min-w-0 flex-1 whitespace-pre-wrap break-words text-[13px] text-[var(--color-surface-800)]">
                      {r.remark}
                    </span>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  <Textarea
                    autoGrow
                    minRows={1}
                    maxRows={5}
                    size="sm"
                    aria-label="Approval remark"
                    placeholder="Add a remark (optional to approve, required to decline)…"
                    value={remarks[r.id] ?? ""}
                    onChange={(e) => setRemarks((prev) => ({ ...prev, [r.id]: e.target.value }))}
                    className="min-w-0 flex-1"
                  />
                  <AsyncButton size="sm" variant="success" onClick={() => decide(r.id, "approve")}>Approve</AsyncButton>
                  <AsyncButton size="sm" variant="destructive" onClick={() => decide(r.id, "reject")}>Decline</AsyncButton>
                </div>

                <p className="m-0 mt-2 text-[11px] text-[var(--color-surface-500)]">
                  Approving makes these their shift for {days === 1 ? "that day" : "those days"} &mdash;
                  late, short and overtime are judged against it. Their usual shift returns afterwards.
                </p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default ShiftDayRequestQueue;
